import { FC, FormEvent, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Checkbox, Flex, Text, TextField } from "@radix-ui/themes";

import { Button } from "@/components/button";

interface NotesSearchFormProps {
  initialQuery?: string;
  initialSpaceAccountId?: string;
  initialIncludeTrashed?: boolean;
  initialIncludeSoftDeleted?: boolean;
  onSearch: (params: {
    query: string;
    spaceAccountId: string;
    includeTrashed: boolean;
    includeSoftDeleted: boolean;
  }) => void;
}

export const NotesSearchForm: FC<NotesSearchFormProps> = ({
  initialQuery = "",
  initialSpaceAccountId = "",
  initialIncludeTrashed = false,
  initialIncludeSoftDeleted = false,
  onSearch,
}) => {
  const [queryValue, setQueryValue] = useState<string>(initialQuery);
  const [spaceAccountIdValue, setSpaceAccountIdValue] = useState<string>(initialSpaceAccountId);
  const [includeTrashed, setIncludeTrashed] = useState<boolean>(initialIncludeTrashed);
  const [includeSoftDeleted, setIncludeSoftDeleted] = useState<boolean>(initialIncludeSoftDeleted);

  useEffect(() => {
    setQueryValue(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setSpaceAccountIdValue(initialSpaceAccountId);
  }, [initialSpaceAccountId]);

  useEffect(() => {
    setIncludeTrashed(initialIncludeTrashed);
  }, [initialIncludeTrashed]);

  useEffect(() => {
    setIncludeSoftDeleted(initialIncludeSoftDeleted);
  }, [initialIncludeSoftDeleted]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch({
      query: queryValue.trim(),
      spaceAccountId: spaceAccountIdValue.trim(),
      includeTrashed,
      includeSoftDeleted,
    });
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <TextFieldContainer
        placeholder="Space Account ID (optional)"
        value={spaceAccountIdValue}
        onChange={event => setSpaceAccountIdValue(event.currentTarget.value)}
        maxLength={36}
        aria-label="Filter by Space Account ID"
      />
      <TextFieldContainer
        placeholder="Search notes by label or UUID"
        value={queryValue}
        onChange={event => setQueryValue(event.currentTarget.value)}
        maxLength={120}
        aria-label="Search Notes"
      />
      <CheckboxRow>
        <Checkbox
          checked={includeTrashed}
          onCheckedChange={value => setIncludeTrashed(value === true)}
          id="include-trashed-notes"
        />
        <Text as="label" htmlFor="include-trashed-notes">
          Include trashed notes
        </Text>
      </CheckboxRow>
      <CheckboxRow>
        <Checkbox
          checked={includeSoftDeleted}
          onCheckedChange={value => setIncludeSoftDeleted(value === true)}
          id="include-soft-deleted-notes"
        />
        <Text as="label" htmlFor="include-soft-deleted-notes">
          Include soft-deleted notes
        </Text>
      </CheckboxRow>
      <Button type="submit">Search</Button>
    </FormContainer>
  );
};

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
  max-width: 480px;
  margin-bottom: 1.5rem;
`;

const TextFieldContainer = styled(TextField.Root)`
  width: 100%;
`;

const CheckboxRow = styled(Flex)`
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;
